import React, { useState, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useTranslation } from 'react-i18next'
import { authService } from '@/services/authService'
import { resolveApiErrorMessage } from '@/utils/apiErrorMessage'
import { useAuth } from '@/contexts/AuthContext'
import { useEdition } from '@/hooks/useEdition'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import { RespondentPackagePanel } from '@/components/researcher/team/UsagePanel'
import type { UserProfile } from '@/types/auth'

/**
 * The fields this page edits. Billing details are here in both editions —
 * FaceWise Pro is sold with an invoice too — while the academic prefix and
 * suffix serve a research programme and are hidden from FaceWise below.
 */
type ProfileFormField =
  | 'first_name'
  | 'last_name'
  | 'phone_number'
  | 'prefix'
  | 'suffix'
  | 'company_name'
  | 'tax_id'
  | 'billing_address'
  | 'billing_postal_code'
  | 'billing_city'
  | 'billing_country'

type ProfileForm = Record<ProfileFormField, string>

const EMPTY_FORM: ProfileForm = {
  first_name: '',
  last_name: '',
  phone_number: '',
  prefix: '',
  suffix: '',
  company_name: '',
  tax_id: '',
  billing_address: '',
  billing_postal_code: '',
  billing_city: '',
  billing_country: '',
}

/** Read the editable fields back off a loaded profile. */
function formFrom(profile: UserProfile): ProfileForm {
  const form = { ...EMPTY_FORM }
  for (const key of Object.keys(EMPTY_FORM) as ProfileFormField[]) {
    form[key] = profile[key] ?? ''
  }
  return form
}

export default function ProfilePage() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('auth')
  const { t: tCommon } = useTranslation('common')
  const { t: tErrors } = useTranslation('errors')
  const { t: tNav } = useTranslation('dashboard')
  const { user, currentView } = useAuth()
  const { isBasic } = useEdition()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [formData, setFormData] = useState<ProfileForm>(EMPTY_FORM)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<string>('')
  const [operation, setOperation] = useState<'view' | 'edit' | 'create'>('view')

  useEffect(() => {
    void loadProfile()
  }, [])

  const loadProfile = async () => {
    setIsLoading(true)
    setError('')
    try {
      const profileData = await authService.getProfile()
      setProfile(profileData)
      setFormData(formFrom(profileData))
      setOperation('view')
    } catch (err: unknown) {
      console.error('Failed to load profile:', err)
      if (err instanceof Error && 'status' in err && (err as { status: number }).status === 404) {
        setOperation('create')
        setError(t('profileNotFound'))
      } else {
        setError(resolveApiErrorMessage(err, tErrors) || t('profileLoadFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name as ProfileFormField]: value,
    }))
    if (error) setError('')
    if (success) setSuccess('')
  }

  const handleCreate = async () => {
    setIsSaving(true)
    setError('')
    setSuccess('')
    try {
      const createdProfile = await authService.createProfile(formData)
      setProfile(createdProfile)
      setOperation('view')
      setSuccess(t('profileCreated'))
    } catch (err: unknown) {
      setError(resolveApiErrorMessage(err, tErrors) || t('profileCreateFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handlePatch = async () => {
    setIsSaving(true)
    setError('')
    setSuccess('')
    try {
      const patchedProfile = await authService.patchProfile(formData)
      setProfile(patchedProfile)
      setOperation('view')
      setSuccess(t('profileUpdated'))
    } catch (err: unknown) {
      setError(resolveApiErrorMessage(err, tErrors) || t('profileUpdateFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleEdit = () => {
    setOperation('edit')
    setError('')
    setSuccess('')
  }

  const handleCancel = () => {
    if (profile) {
      setFormData(formFrom(profile))
      setOperation('view')
    } else {
      setOperation('create')
    }
    setError('')
    setSuccess('')
  }

  const isView = operation === 'view'
  const fullName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.email || user?.email || ''
  const initials = (profile?.first_name?.[0] ?? profile?.email?.[0] ?? user?.email?.[0] ?? '?').toUpperCase()
  // The chip reflects the currently selected view (every account is both).
  const roleLabel = currentView === 'researcher' ? tNav('nav.roleResearcher') : tNav('nav.roleRespondent')

  const cardSx = {
    bgcolor: c.white,
    border: `1px solid ${c.ink08}`,
    borderRadius: `${theme.affexy.radii.lg}px`,
    boxShadow: '0 1px 2px rgba(27,28,56,.04)',
  }

  const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: `${theme.affexy.radii.sm}px`, bgcolor: c.white },
  }

  return (
    <ResearcherShell crumbs={[t('profileTitle')]} hideTopBarAction>
      <Box sx={{ maxWidth: 880, mx: 'auto', width: '100%', px: { xs: 2, md: 0 } }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
            <CircularProgress />
            <Typography sx={{ color: c.ink56 }}>{t('loadingProfile')}</Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {/* Identity header */}
            <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 }, display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 999,
                  background: theme.affexy.gradients.coral,
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: theme.affexy.fonts.heading,
                  fontWeight: 700,
                  fontSize: 24,
                  flexShrink: 0,
                }}
              >
                {initials}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h4" sx={{ fontSize: 24, lineHeight: 1.2 }}>
                  {fullName}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 14, color: c.ink56 }}>{profile?.email || user?.email}</Typography>
                  <Chip
                    label={roleLabel}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 600,
                      color: c.coral,
                      bgcolor: c.coralSoft,
                    }}
                  />
                </Stack>
              </Box>
              {isView && profile && (
                <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={handleEdit}>
                  {t('editProfile')}
                </Button>
              )}
              {operation === 'create' && <Chip label={t('createNewProfile')} color="primary" />}
              {operation === 'edit' && <Chip label={t('editingProfile')} variant="outlined" />}
            </Box>

            {error && (
              <Alert severity="error" sx={{ borderRadius: `${theme.affexy.radii.md}px` }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ borderRadius: `${theme.affexy.radii.md}px` }}>
                {success}
              </Alert>
            )}

            {/* Package standing. FaceWise has no Team page, where the Pro
                minutes panel lives, so the account screen is where a customer
                can actually find out how many respondents are left. */}
            {isBasic && <RespondentPackagePanel />}

            {/* Details form */}
            <Box sx={{ ...cardSx, p: { xs: 2.5, md: 3 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: c.ink56, mb: 2.5 }}>
                {t('profileTitle')}
              </Typography>

              <Stack spacing={2.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    id="email"
                    label={tCommon('email')}
                    value={profile?.email || user?.email || ''}
                    disabled
                    helperText={t('emailCannotChange')}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    id="id"
                    label={t('profileId')}
                    value={profile?.id || 'N/A'}
                    disabled
                    sx={fieldSx}
                  />
                </Box>

                <Divider sx={{ borderColor: c.ink08 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    id="first_name"
                    label={t('firstName')}
                    name="first_name"
                    value={formData.first_name || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    id="last_name"
                    label={t('lastName')}
                    name="last_name"
                    value={formData.last_name || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    id="phone_number"
                    label={t('phoneNumber')}
                    name="phone_number"
                    value={formData.phone_number || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                  {/* Academic prefix and suffix sign a research programme's
                      reports; FaceWise has no use for them. */}
                  {!isBasic && (
                    <>
                      <TextField
                        fullWidth
                        id="prefix"
                        label={t('prefix')}
                        name="prefix"
                        value={formData.prefix || ''}
                        onChange={handleInputChange}
                        disabled={isView}
                        helperText={t('prefixHint')}
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        id="suffix"
                        label={t('suffix')}
                        name="suffix"
                        value={formData.suffix || ''}
                        onChange={handleInputChange}
                        disabled={isView}
                        helperText={t('suffixHint')}
                        sx={fieldSx}
                      />
                    </>
                  )}
                </Box>

                <Divider sx={{ borderColor: c.ink08 }} />

                {/* Billing details — both editions are sold with an invoice. */}
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: c.ink56 }}>
                    {tCommon('billing.title')}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: c.ink56, mt: 0.5 }}>
                    {tCommon('billing.hint')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    id="company_name"
                    label={tCommon('billing.companyName')}
                    name="company_name"
                    value={formData.company_name || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    id="tax_id"
                    label={tCommon('billing.taxId')}
                    name="tax_id"
                    value={formData.tax_id || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    id="billing_address"
                    label={tCommon('billing.address')}
                    name="billing_address"
                    value={formData.billing_address || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                  />
                  <TextField
                    fullWidth
                    id="billing_postal_code"
                    label={tCommon('billing.postalCode')}
                    name="billing_postal_code"
                    value={formData.billing_postal_code || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    id="billing_city"
                    label={tCommon('billing.city')}
                    name="billing_city"
                    value={formData.billing_city || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                  <TextField
                    fullWidth
                    id="billing_country"
                    label={tCommon('billing.country')}
                    name="billing_country"
                    value={formData.billing_country || ''}
                    onChange={handleInputChange}
                    disabled={isView}
                    sx={fieldSx}
                  />
                </Box>
              </Stack>

              {!isView && (
                <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
                    {tCommon('cancel')}
                  </Button>
                  {operation === 'create' && (
                    <Button variant="contained" onClick={handleCreate} disabled={isSaving}>
                      {isSaving ? <CircularProgress size={22} color="inherit" /> : t('createProfile')}
                    </Button>
                  )}
                  {operation === 'edit' && (
                    <Button variant="contained" onClick={handlePatch} disabled={isSaving}>
                      {isSaving ? <CircularProgress size={22} color="inherit" /> : t('updateProfile')}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        )}
      </Box>
    </ResearcherShell>
  )
}
